import { API_BASE } from '@/shared/services/api';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Camera, Check, X, RotateCcw, DoorOpen, Hand, Lock,
  ChevronLeft, AlertCircle
} from 'lucide-react';
import { MobileButton, MobileCard } from '../components/MobileUIKit';
import PhotoCapture from '../components/PhotoCapture';
import { useDriver } from '../context/DriverContext';
import { deliveryAPI } from '@/shared/services/api';
import '../styles/mobile.css';

const API_URL = API_BASE;

// Icon mapping for dynamically loaded icons
const ICON_MAP = {
  DoorOpen, Hand, Lock
};

export const DriverPODCapture = () => {
  const navigate = useNavigate();
  const { activeRoute, currentStop, geoLocation, completeDelivery, queuePOD, isOnline } = useDriver();
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [photo, setPhoto] = useState(null);
  const [signature, setSignature] = useState(null);
  const [recipientName, setRecipientName] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const stop = currentStop || activeRoute?.stops?.find(s => s.status === 'ARRIVED') || activeRoute?.stops?.[0] || null;
  const stopId = stop?.stop_id || stop?.stopId || stop?.id || 'STOP-DEMO-001';
  const orderLabel = stop?.order_number || stop?.orderNumber || stop?.shipmentId || 'ORD-2026-4521';
  const destination = stop?.address || stop?.destination || stop?.customer_address || '456 Oak Avenue, Apt 3B';

  // Fetch delivery methods from API
  useEffect(() => {
    const fetchDeliveryMethods = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reference/pod-methods`);
        if (response.ok) {
          const data = await response.json();
          // Map to local format with icons
          const methods = data.slice(0, 3).map(m => ({
            id: m.code.toLowerCase(),
            icon: ICON_MAP[m.icon] || DoorOpen,
            label: m.label
          }));
          setDeliveryMethods(methods);
          if (methods.length > 0) {
            setSelectedMethod(methods[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch POD methods:', error);
        // Fallback
        setDeliveryMethods([
          { id: 'door', icon: DoorOpen, label: 'Leave at Door' },
          { id: 'hand', icon: Hand, label: 'Hand to Customer' },
          { id: 'locker', icon: Lock, label: 'Locker/Safe' },
        ]);
        setSelectedMethod('door');
      }
    };
    fetchDeliveryMethods();
  }, []);

  const handleRetakePhoto = () => {
    setPhoto(null);
  };

  // Signature pad handlers
  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = 'var(--mobile-text-primary)';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const saveSignature = () => {
    if (!canvasRef.current) return;
    setSignature(canvasRef.current.toDataURL('image/png'));
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  };

  const handleSubmit = async () => {
    if (!photo) {
      setSubmitError('Please take a photo first');
      return;
    }
    if (selectedMethod === 'hand' && !signature) {
      setSubmitError('Please capture the customer signature first');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    const podPayload = {
      stop_id: stopId,
      recipient_name: recipientName || 'Recipient',
      signature: signature || null,
      photo,
      notes,
      delivery_method: selectedMethod,
      gps_lat: geoLocation?.lat,
      gps_lng: geoLocation?.lng,
      gps_accuracy: geoLocation?.accuracy,
      captured_at: new Date().toISOString(),
      scanned_packages: stop?.packages?.map(pkg => pkg.package_id || pkg.id).filter(Boolean) || []
    };

    try {
      if (!isOnline) {
        queuePOD(podPayload);
      } else {
        await deliveryAPI.capturePOD(podPayload);
      }
      completeDelivery(stopId, podPayload);
      navigate('/driver/success');
    } catch (error) {
      console.error('Failed to submit POD:', error);
      queuePOD({ ...podPayload, sync_error: error.message });
      setSubmitError('POD saved locally for sync. Backend submission did not complete.');
      navigate('/driver/success');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mobile-app min-h-screen mobile-safe-top">
      {/* Header */}
      <div className="mobile-px-4 pt-4 pb-3 mobile-flex mobile-items-center mobile-gap-3">
        <button 
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: 'var(--mobile-bg-tertiary)' }}
        >
          <ChevronLeft className="w-5 h-5" style={{ color: 'var(--mobile-text-primary)' }} />
        </button>
        <h1 className="mobile-text-title" style={{ color: 'var(--mobile-text-primary)' }}>
          Delivery Confirmation
        </h1>
      </div>

      <div className="mobile-px-4 pb-8">
        {/* Delivery Info */}
        <MobileCard className="mb-4">
          <p className="mobile-text-tiny" style={{ color: 'var(--mobile-text-tertiary)' }}>
            DELIVERING TO
          </p>
          <p 
            className="font-semibold mt-1"
            style={{ color: 'var(--mobile-text-primary)' }}
          >
            {destination}
          </p>
          <p className="mobile-text-caption">{recipientName || 'Recipient'} • Order #{orderLabel}</p>
        </MobileCard>

        {/* Delivery Method */}
        <div className="mb-6">
          <p 
            className="mobile-text-caption font-medium mb-3"
            style={{ color: 'var(--mobile-text-secondary)' }}
          >
            DELIVERY METHOD
          </p>
          <div className="mobile-flex mobile-gap-2">
            {deliveryMethods.map(method => {
              const Icon = method.icon;
              const isSelected = selectedMethod === method.id;
              return (
                <button
                  key={method.id}
                  onClick={() => setSelectedMethod(method.id)}
                  className="flex-1 flex flex-col items-center py-4 rounded-xl transition-all"
                  style={{
                    background: isSelected ? 'var(--ezp-primary)' : 'var(--mobile-bg-secondary)',
                    border: `2px solid ${isSelected ? 'var(--ezp-primary)' : 'var(--mobile-border)'}`,
                  }}
                >
                  <Icon 
                    className="w-6 h-6 mb-2" 
                    style={{ color: isSelected ? 'white' : 'var(--mobile-text-secondary)' }}
                  />
                  <span 
                    className="text-xs font-medium"
                    style={{ color: isSelected ? 'white' : 'var(--mobile-text-secondary)' }}
                  >
                    {method.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Photo Capture */}
        <div className="mb-6">
          <p 
            className="mobile-text-caption font-medium mb-3 mobile-flex mobile-items-center mobile-gap-2"
            style={{ color: 'var(--mobile-text-secondary)' }}
          >
            <Camera className="w-4 h-4" />
            PHOTO PROOF (Required)
          </p>
          
          <PhotoCapture
            title="Photo Proof"
            subtitle="Take a real camera photo showing the delivered packages"
            onCapture={setPhoto}
            onClear={handleRetakePhoto}
          />
        </div>

        {/* Signature Pad */}
        {selectedMethod === 'hand' && (
          <div className="mb-6 mobile-animate-fade-in">
            <div className="mobile-flex mobile-justify-between mobile-items-center mb-3">
              <p 
                className="mobile-text-caption font-medium mobile-flex mobile-items-center mobile-gap-2"
                style={{ color: 'var(--mobile-text-secondary)' }}
              >
                CUSTOMER SIGNATURE
              </p>
              {signature && (
                <button
                  onClick={clearSignature}
                  className="text-xs font-medium"
                  style={{ color: 'var(--ezp-primary)' }}
                >
                  Clear
                </button>
              )}
            </div>
            
            <div 
              className="rounded-xl overflow-hidden"
              style={{ 
                background: 'var(--mobile-bg-secondary)',
                border: '2px dashed var(--mobile-border)'
              }}
            >
              <canvas
                ref={canvasRef}
                width={350}
                height={150}
                className="w-full touch-none"
                style={{ 
                  background: 'var(--mobile-bg-secondary)',
                }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              {!signature && (
                <p 
                  className="text-center py-4 mobile-text-caption"
                  style={{ color: 'var(--mobile-text-disabled)' }}
                >
                  Sign here
                </p>
              )}
            </div>
            <MobileButton
              variant="secondary"
              fullWidth
              size="medium"
              icon={Check}
              disabled={!isDrawing && !canvasRef.current}
              onClick={saveSignature}
            >
              Save Signature
            </MobileButton>
          </div>
        )}

        {/* Notes (optional) */}
        <div className="mb-6">
          <p 
            className="mobile-text-caption font-medium mb-3"
            style={{ color: 'var(--mobile-text-secondary)' }}
          >
            DELIVERY NOTES (Optional)
          </p>
          <textarea
            placeholder="Add any notes about the delivery..."
            className="w-full p-3 rounded-xl resize-none"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{
              background: 'var(--mobile-bg-secondary)',
              border: '1px solid var(--mobile-border)',
              color: 'var(--mobile-text-primary)',
            }}
          />
        </div>

        {/* Warning */}
        {!photo && (
          <div 
            className="mobile-flex mobile-items-center mobile-gap-2 p-3 rounded-lg mb-4"
            style={{ background: 'var(--mobile-warning-muted)' }}
          >
            <AlertCircle className="w-4 h-4" style={{ color: 'var(--mobile-warning)' }} />
            <p className="mobile-text-tiny" style={{ color: 'var(--mobile-warning)' }}>
              Photo is required to confirm delivery
            </p>
          </div>
        )}

        {submitError && (
          <div
            className="mobile-flex mobile-items-center mobile-gap-2 p-3 rounded-lg mb-4"
            style={{ background: 'var(--mobile-warning-muted)' }}
          >
            <AlertCircle className="w-4 h-4" style={{ color: 'var(--mobile-warning)' }} />
            <p className="mobile-text-tiny" style={{ color: 'var(--mobile-warning)' }}>
              {submitError}
            </p>
          </div>
        )}

        {/* Submit Button */}
        <MobileButton
          variant="success"
          fullWidth
          size="large"
          icon={Check}
          loading={isSubmitting}
          disabled={!photo}
          onClick={handleSubmit}
        >
          Confirm Delivery
        </MobileButton>
      </div>
    </div>
  );
};

export default DriverPODCapture;
